import { useState, useEffect } from 'react';
import { useChat } from '@/app/hooks/useChat';
import { useModuleConfig } from '@/app/hooks/useModuleConfig';
import { configLoader } from '@/lib/config-loader';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import type { LearnerState, SlideConfig } from '@/lib/types';

interface ChatPanelProps {
  learnerId: string;
  moduleId: string;
  state: LearnerState;
}

export function ChatPanel({ learnerId, moduleId, state }: ChatPanelProps) {
  const { config: moduleConfig } = useModuleConfig(moduleId);
  const [slides, setSlides] = useState<SlideConfig[]>([]);

  // Get current part info
  const currentPartIndex = state.currentPosition.partIndex;
  const currentPhase = state.currentPosition.phase;
  const currentSlideIndex = state.currentPosition.slideIndex;
  const partId = `part-${currentPartIndex + 1}`;

  // Determine which agent to use based on phase
  const agentPhase = currentPhase === 'content' ? 'companion' : currentPhase;

  // Get current slide title for context-aware companion
  const currentSlide = slides[currentSlideIndex];
  const currentSlideTitle = currentSlide?.title;

  const {
    messages,
    streamingMessage,
    isLoading,
    error,
    turnCount,
    sendMessage,
  } = useChat({
    learnerId,
    moduleId,
    partId,
    phase: agentPhase as any,
    slideIndex: currentSlideIndex,
    slideTitle: currentSlideTitle,
  });

  // Load slides to get companionPrompts
  useEffect(() => {
    async function loadSlides() {
      try {
        const slidesConfig = await configLoader.loadPartSlides(moduleId, partId);
        setSlides(slidesConfig.slides);
      } catch (err) {
        console.error('Failed to load slides for companion prompts:', err);
      }
    }
    loadSlides();
  }, [moduleId, partId]);

  // Get suggested questions from current slide
  const suggestedQuestions = currentSlide?.companionPrompts || [];

  // Get max turns from config (default to 10)
  const maxTurns = 10;
  const isAtLimit = turnCount >= maxTurns;

  // Handle suggestion click — send immediately
  const handleSuggestionClick = (question: string) => {
    if (!isLoading && !isAtLimit) {
      sendMessage(question);
    }
  };

  return (
    <div className="h-full flex flex-col max-h-full overflow-hidden">
      {/* Header with turn counter */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif font-semibold text-lg">Learning Companion</h3>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Turn {turnCount} of {maxTurns}
          </div>
        </div>

        {moduleConfig && (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Part {currentPartIndex + 1}: {moduleConfig.parts[currentPartIndex].title}
          </p>
        )}

        {/* Warning near limit */}
        {turnCount >= maxTurns - 2 && turnCount < maxTurns && (
          <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
            Approaching conversation limit
          </div>
        )}

        {/* At limit */}
        {isAtLimit && (
          <div className="mt-2 text-xs text-red-600 dark:text-red-400">
            Conversation limit reached
          </div>
        )}
      </div>

      {/* Welcome message + suggested questions when no messages */}
      {messages.length === 0 && !isLoading && (
        <div className="p-4 accent-bg-lighter border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <p className="text-sm text-slate-800 dark:text-slate-200 mb-3">
            I'm here to help you think through the concepts as you learn. Ask me anything, or try one of these:
          </p>
          {suggestedQuestions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => handleSuggestionClick(q)}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/70 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-colors cursor-pointer text-left"
                >
                  {q}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Slide-context suggested questions (when there are messages but slide has suggestions) */}
      {messages.length > 0 && suggestedQuestions.length > 0 && !isLoading && !isAtLimit && (
        <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700/50 flex-shrink-0">
          <p className="text-xs text-slate-400 dark:text-slate-500 mb-1.5">Try asking:</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestedQuestions.slice(0, 2).map((q, i) => (
              <button
                key={i}
                onClick={() => handleSuggestionClick(q)}
                className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer truncate max-w-[200px]"
                title={q}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-800 flex-shrink-0">
          <p className="text-sm text-red-900 dark:text-red-200">
            {error.message}
          </p>
        </div>
      )}

      {/* Message list - takes remaining space and scrolls */}
      <div className="flex-1 overflow-hidden min-h-0">
        <ChatMessageList
          messages={messages}
          streamingMessage={streamingMessage}
          isLoading={isLoading}
        />
      </div>

      {/* Input - fixed at bottom */}
      <div className="flex-shrink-0">
        <ChatInput
          onSendMessage={sendMessage}
          disabled={isLoading || isAtLimit}
          placeholder={isAtLimit ? 'Conversation limit reached' : 'Ask a question...'}
        />
      </div>
    </div>
  );
}
